import { MdbCompatibleOption, ModuleStatus, Truth } from "./common/common-types";
import { DateTime } from "luxon";

export interface ApiClientData {
    companyName: string;
    applicationName: string;
    description: string;
    user_id: number;
    created: string | DateTime;
    logo: string;
    refresh_token: string;
}

export interface ApiAccessData {
    apiId: string;
    apiEnabled: Truth;
}

export interface TestEmailData {
    people: string,
    other: string[]
}

export interface CurriculumData {
    name: string;
    id: number;
    active: number;
    forceOrder: number;
}

export interface CurriculumListData {
    Curriculum: CurriculumData
}

export interface SafetyReportsListData {

}

export interface ModuleOption extends MdbCompatibleOption {
    category: string;
}

export interface ModuleListOptionsData {
    includePublished?: boolean;
    includeDraft?: boolean;
    includeArchived?: boolean;
    filterExpress?: boolean;
}

export interface UserProfileData {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    avatar: string;
}

export interface UserAddressData {
  id: number;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

export interface BaseAssignmentInfo {
    max_time_extension: number;
    practical: number;
    status: number;
    calc_percent_complete: number;
    spent_time_seconds: number;
    userID: number;

    date_assigned: DateTime;
    due_date: DateTime;
    course_start_date: DateTime;
    course_end_date: DateTime;
    course_available_date: DateTime;
    online_completion_date: DateTime;
    retained_expiry?: DateTime;
    expiryDate?: DateTime;
    isValidUntil?: boolean;
    hideDue?: boolean;
    isAvailable: boolean;
    tokens?: object;
    isExpress: boolean;
    token?: string;

    module: BaseModuleInfo;
}

export interface BaseModuleInfo {
    courseID: number;
    course_name: string;
    status: ModuleStatus;
    course_order?: number;
}

export class ModuleProgressInfo {
    info: ModuleOption;
    completeAssignmentsCount: number = 0;
    totalAssignmentsCount: number = 0;
    progressPercentage: number = 0;
    name: string = "";

    constructor(public module: ModuleOption) {
        this.info = module;
        this.name = module.label;
    }
}

export class CurriculumListItem {
    constructor(public name: string, public id: number, public active: boolean) {}
}

export class Curriculum {
    info: CurriculumData = null;
    fullModuleList: BaseModuleInfo[] = [];
    assignments: Map<number, BaseAssignmentInfo> = new Map<number, BaseAssignmentInfo>();
    moduleOrder: Map<number, number> = new Map<number, number>();

    completeAssignments: BaseAssignmentInfo[] = [];
    incompleteAssignments: BaseAssignmentInfo[] = [];
    missingModules: BaseModuleInfo[] = [];
    incompleteAssignmentCount: number = 0;
    completeAssignmentsCount: number = 0;
    totalAssignmentsCount: number = 0;
    progressPercentage: number = 0;
    hasCurriculum: boolean = false;
    availableDate: DateTime | null = null;
    dueDate: DateTime | null = null;
    
    constructor() {}

    get moduleList(): number[] {
        return this.fullModuleList.filter(module => module.status == ModuleStatus.Published).map(module => module.courseID);
    }    

    // get hasCurriculum(): boolean {
    //     return this.moduleList.every(moduleId => this.assignments.has(moduleId));
    // }

    hasDueModules() {
        return this.incompleteAssignments.some(assignment => assignment.due_date.year != 0);
    }

    isOverdue() {
        let today: DateTime = DateTime.fromJSDate(new Date());

        return this.incompleteAssignments.some(assignment => assignment.due_date.year != 0 && assignment.due_date < today);
    }

    addModule(assignment: BaseAssignmentInfo) {
        this.assignments.set(assignment.module.courseID, assignment);

        let moduleOrder = this.fullModuleList.find(module => module.courseID == assignment.module.courseID);
        if (moduleOrder) this.moduleOrder.set(assignment.module.courseID, moduleOrder.course_order);

        if (assignment.course_end_date.year != 0) this.completeAssignments.push(assignment);
        else this.incompleteAssignments.push(assignment);
    }

    getCompletedCount(): number {
        return Array.from(this.assignments.values()).filter(assignment => assignment.course_end_date.year != 0).length;
    }

    sortModules() {
        this.completeAssignments.sort((a,b) => this.moduleOrder.get(a.module.courseID) - this.moduleOrder.get(b.module.courseID));
        this.incompleteAssignments.sort((a,b) => this.moduleOrder.get(a.module.courseID) - this.moduleOrder.get(b.module.courseID));
    }

    computeMissingModules() {
        this.missingModules = this.fullModuleList
            .filter(module => module.status == ModuleStatus.Published && !this.assignments.has(module.courseID));
    }

    nextAllowedModule() {
        if (this.incompleteAssignments.length) return this.incompleteAssignments[0];
    }
}

export interface QualityRatings {
    content: number;
    training: number;
}

export interface HybridLearningCenterData {
    Network: {
        networkID: number;
        network_name: string;
        corporate_logo: string;
        hybridAccessUserId?: number;
    },
    users: {
        userID: number;
        email: string;
        hybridAccess: Truth;
    },
    user_info: {
        first_name: string;
        last_name: string;
        avatar: string;
    }
}

export interface HybridLearningCenter {
    networkID: number;
    networkName: string;
    corporateLogo: string;
    hybridAccessUserId?: number;
    registrars: HybridLearningCenterUser[];
}

export interface HybridLearningCenterUser {
    userID: number;
    email: string;
    firstName: string;
    lastName: string;
    avatar: string;
    hybridAccess: Truth;
}

export interface HybridLearningCenterSaveData {
    User: {
        userID: number;
        hybridAccess: Truth;
    }
}

export interface LearningCenterVendorAccessData {
    Network: {
        networkID: number;
        hybridAccessUserId: number;
    }
}

export interface SupportContactData {
    email: string;
    phoneNumber: string;
    name: string;
}